import type { WikiArticle } from './wikiTypes'

export const llrArticles: WikiArticle[] = [
  {
    id: 'llr-1',
    categoryId: 'llr', categoryLabel: 'Local Lead Radar', categoryColor: '#C9A84C',
    moduleId: 'llr', guideNumber: 1, totalGuides: 3,
    title: 'Créer et configurer un Radar',
    subtitle: 'Choisir une zone géographique, définir les mots-clés et les seuils d\'alerte.',
    readTime: '8 min',
    nextArticle: 'llr-2',
    sections: [
      {
        title: 'À quoi sert un Radar ?',
        content: 'Un Radar est le cœur du module Local Lead Radar. C\'est une surveillance permanente et automatique d\'un marché local défini par vous : une combinaison de mots-clés et d\'une zone géographique. Dès que la demande dépasse un seuil que vous avez fixé, TremorApp vous alerte immédiatement.\n\nUn Radar répond à la question : « Est-ce que des gens cherchent mon service dans ma zone en ce moment ? »',
        tip: { type: 'example', content: 'Un Radar nommé "Plombiers Paris 15e" surveille les recherches pour "plombier urgence", "dépannage plomberie" et "fuite eau Paris" dans le 15e arrondissement. Quand le volume dépasse 200 recherches par semaine, une alerte est envoyée.' },
        screenshotHint: '📸 Capture : page Radar Hub montrant la liste des radars avec leurs scores d\'intensité',
      },
      {
        title: 'Étape 1 — Accéder à la création',
        content: 'Deux points d\'entrée sont disponibles :\n\n• Sidebar gauche → cliquez sur "Nouveau Radar" (icône + dans le menu LLR)\n• Page Radar Hub → bouton "+ Nouveau Radar" en haut à droite\n\nLe formulaire de création s\'ouvre en pleine page.',
        screenshotHint: '📸 Capture : bouton "+ Nouveau Radar" en haut à droite de la page Radar Hub',
      },
      {
        title: 'Étape 2 — Nommer votre Radar',
        content: 'Choisissez un nom clair et mémorable. Il apparaît dans toutes les listes, alertes et rapports.',
        table: {
          headers: ['Format recommandé', 'Exemple', 'À éviter'],
          rows: [
            ['[Secteur] [Zone]', 'Plombiers Paris 15e', 'Radar 1, Test'],
            ['[Service] [Ville]', 'Rénovation cuisine Lyon', 'Nouveau, Urgent'],
            ['[Client] [Activité]', 'Cabinet Dupont — Kinés Bordeaux', 'A, B, C, 01'],
          ],
        },
      },
      {
        title: 'Étape 3 — Définir la zone géographique',
        content: 'La zone géographique détermine le périmètre de surveillance. TremorApp propose trois niveaux de précision.',
        table: {
          headers: ['Niveau', 'Description', 'Cas d\'usage'],
          rows: [
            ['Ville', 'Surveillance sur une ville entière', 'PME locale avec clientèle ville-entière'],
            ['Arrondissement / Quartier', 'Précision infra-ville', 'Commerce de proximité, prestataire de niche'],
            ['Rayon en km', 'Cercle autour d\'un point GPS', 'Artisan itinérant, livraison à domicile'],
          ],
        },
        tip: { type: 'tip', content: 'Une zone trop large (ex : "Île-de-France entière") génère beaucoup de données mais peu d\'alertes actionnables. Une zone de 5 à 15 km autour de votre zone d\'activité produit des alertes plus pertinentes.' },
        steps: [
          { number: 1, title: 'Saisir la zone', description: 'Tapez le nom de la ville ou du quartier dans le champ "Zone"' },
          { number: 2, title: 'Sélectionner', description: 'TremorApp propose des suggestions en temps réel — sélectionnez la bonne' },
          { number: 3, title: 'Rayon (optionnel)', description: 'Cochez "Définir un rayon" et entrez la distance en km' },
          { number: 4, title: 'Vérifier', description: 'La carte de prévisualisation se met à jour pour confirmer la zone' },
        ],
        screenshotHint: '📸 Capture : formulaire de zone géographique avec la carte de prévisualisation',
      },
      {
        title: 'Étape 4 — Ajouter les mots-clés',
        content: 'Les mots-clés sont les requêtes que vous souhaitez surveiller. TremorApp analyse leur volume de recherche dans la zone définie.',
        table: {
          headers: ['Type', 'Exemples', 'Pertinence'],
          rows: [
            ['Requêtes d\'urgence', 'plombier urgence nuit, serrurier urgent', 'Très haute — intention d\'achat immédiate'],
            ['Requêtes de service', 'installation chaudière, rénovation salle de bain', 'Haute — projet en cours'],
            ['Requêtes locales', 'plombier Paris 15, chauffagiste Vincennes', 'Haute — intention géolocalisée'],
            ['Requêtes informatives', 'prix plombier, tarif dépannage', 'Moyenne — prospect en recherche'],
          ],
        },
        tip: { type: 'tip', content: 'Commencez avec 3 à 8 mots-clés par radar. Au-delà de 15, le volume d\'alertes peut devenir difficile à gérer. Créez plusieurs radars thématiques plutôt qu\'un seul radar avec trop de mots-clés.' },
        screenshotHint: '📸 Capture : champ d\'ajout de mots-clés avec les badges et les suggestions intelligentes',
      },
      {
        title: 'Étape 5 — Configurer les seuils d\'alerte',
        content: 'Les seuils déterminent quand TremorApp vous envoie une alerte. Il existe deux types de seuils.',
        table: {
          headers: ['Type de seuil', 'Description', 'Exemple'],
          rows: [
            ['Volume absolu', 'Alerte si le nombre de recherches dépasse X sur la période', '200 recherches par semaine'],
            ['Variation relative', 'Alerte si le volume augmente de X% par rapport à la moyenne', 'Hausse de 50% vs moyenne 4 semaines'],
          ],
        },
        screenshotHint: '📸 Capture : curseurs de seuil avec la valeur actuelle affichée en temps réel',
      },
      {
        title: 'Étape 6 — Configurer les notifications',
        content: 'Pour chaque Radar, vous pouvez paramétrer indépendamment les canaux de notification : email, notification in-app, et webhook (avancé pour Zapier, n8n, Slack).',
        table: {
          headers: ['Option', 'Comportement', 'Recommandé pour'],
          rows: [
            ['Immédiat', 'Notification dès que le seuil est dépassé', 'Urgences, marchés très réactifs'],
            ['Résumé quotidien', 'Récapitulatif à une heure fixe', 'Suivi régulier sans interruption'],
            ['Résumé hebdomadaire', 'Bilan des tendances de la semaine', 'Analyse stratégique'],
          ],
        },
      },
      {
        title: 'Étape 7 — Créer et activer',
        content: 'Vérifiez le résumé de configuration, cliquez sur "Créer le Radar". TremorApp lance la première analyse (30 secondes à 3 minutes). Le Radar apparaît dans le Radar Hub avec le statut "Actif" et les 7 derniers jours de données historiques.',
        tip: { type: 'info', content: 'Si le score est à 0 après création, cela signifie que votre zone et vos mots-clés n\'ont pas généré de volume significatif récemment — c\'est une information utile en soi.' },
      },
      {
        title: 'Gérer et modifier un Radar',
        content: 'Dans le Radar Hub, cliquez sur la ligne du Radar pour l\'ouvrir, puis "Modifier" en haut à droite. Tous les paramètres sont modifiables.',
        table: {
          headers: ['Action', 'Effet', 'Comment'],
          rows: [
            ['Pause', 'Arrête la surveillance sans supprimer les données', 'Bouton "Pause" dans la fiche'],
            ['Réactiver', 'Reprend depuis le dernier point', 'Bouton "Activer" sur un Radar en pause'],
            ['Supprimer', 'Supprime le Radar et toutes ses données', 'Menu "..." → "Supprimer" + confirmation'],
          ],
        },
      },
    ],
  },
  {
    id: 'llr-2',
    categoryId: 'llr', categoryLabel: 'Local Lead Radar', categoryColor: '#C9A84C',
    moduleId: 'llr', guideNumber: 2, totalGuides: 3,
    title: 'Interpréter les alertes d\'intensité',
    subtitle: 'Comprendre le score d\'intensité, lire une alerte et décider de la bonne réaction.',
    readTime: '6 min',
    prevArticle: 'llr-1', nextArticle: 'llr-3',
    sections: [
      {
        title: 'Le score d\'intensité',
        content: 'Le score d\'intensité est l\'indicateur central du module LLR. C\'est un nombre entre 0 et 100 qui mesure le niveau d\'activité de recherche sur votre marché local à un instant donné, relativement à sa moyenne historique.',
        table: {
          headers: ['Score', 'Interprétation', 'Signal'],
          rows: [
            ['0 – 20', 'Activité très faible, marché calme', 'Pas d\'action urgente'],
            ['20 – 40', 'Activité normale, tendance stable', 'Surveiller l\'évolution'],
            ['40 – 60', 'Au-dessus de la moyenne', 'Considérer une activation légère'],
            ['60 – 80', 'Pic de demande significatif', 'Activer vos dispositifs marketing'],
            ['80 – 100', 'Pic exceptionnel, opportunité rare', 'Réagir immédiatement'],
          ],
        },
        tip: { type: 'info', content: 'Le score est relatif : un score de 75 sur un marché habituellement calme est un signal plus fort que 75 sur un marché naturellement volatil. TremorApp ajuste le calcul à l\'historique de chaque Radar.' },
        screenshotHint: '📸 Capture : badge de score d\'intensité coloré (critique rouge, élevé orange, modéré jaune)',
      },
      {
        title: 'Anatomie d\'une alerte',
        content: 'Chaque alerte générée par un Radar contient trois sections :\n\n**En-tête** — Nom du Radar, date/heure, score d\'intensité, type d\'alerte.\n\n**Corps** — Mot-clé déclencheur, volume détecté, comparaison avec la baseline, graphique miniature sur 14 jours.\n\n**Actions IA** — 1 à 3 actions recommandées avec bouton "Utiliser" et score de confiance.',
        screenshotHint: '📸 Capture : une alerte complète ouverte avec les 3 sections visibles',
      },
      {
        title: 'Lire le graphique d\'intensité',
        content: 'Sur chaque page de Radar, le graphique d\'intensité affiche l\'évolution du score dans le temps.',
        table: {
          headers: ['Élément visuel', 'Signification'],
          rows: [
            ['Ligne teal', 'Score d\'intensité jour par jour'],
            ['Zone grisée', 'Plage normale (25e–75e percentile historique)'],
            ['Point orange', 'Alerte déclenchée à cette date'],
            ['Ligne pointillée', 'Seuil d\'alerte configuré'],
            ['Zone hachurée verte', 'Pic capté — action entreprise'],
          ],
        },
        screenshotHint: '📸 Capture : graphique d\'intensité avec un pic et une alerte marquée',
      },
      {
        title: 'Patterns courants à reconnaître',
        content: 'Apprenez à lire les formes du graphique pour anticiper les opportunités.',
        table: {
          headers: ['Pattern', 'Apparence', 'Signification'],
          rows: [
            ['Pic isolé', 'Spike net suivi d\'un retour rapide', 'Événement ponctuel (météo, actualité)'],
            ['Montée progressive', 'Hausse graduelle sur 5-10 jours', 'Tendance saisonnière en cours'],
            ['Plateau élevé', 'Score maintenu plusieurs jours', 'Marché durablement actif'],
            ['Double pic', 'Deux spikes espacés de 5-7 jours', 'Cycle hebdomadaire amplifié'],
          ],
        },
      },
      {
        title: 'Centre d\'alertes — gérer le flux',
        content: 'Le Centre d\'alertes centralise toutes les alertes de tous vos Radars. Filtrez par Radar, par score, par statut ou par période.',
        table: {
          headers: ['Statut', 'Signification', 'Passer au suivant'],
          rows: [
            ['Non lue', 'Reçue, non consultée', 'Cliquer sur l\'alerte'],
            ['Lue', 'Consultée, action non définie', 'Automatique à l\'ouverture'],
            ['En cours', 'Action IA en utilisation', 'Cliquer "Utiliser" sur une action'],
            ['Traitée', 'Action entreprise, clôturée', 'Cliquer "Marquer comme traitée"'],
            ['Ignorée', 'Signal non pertinent', 'Cliquer "Ignorer" — le seuil sera ajusté'],
          ],
        },
        tip: { type: 'tip', content: 'Traitez ou ignorez chaque alerte dans les 24h. Les alertes ignorées sont analysées pour affiner automatiquement vos seuils — si vous ignorez régulièrement les alertes à 45/100, le système relèvera votre seuil effectif.' },
        screenshotHint: '📸 Capture : Centre d\'alertes avec les filtres et les badges de statut',
      },
    ],
  },
  {
    id: 'llr-3',
    categoryId: 'llr', categoryLabel: 'Local Lead Radar', categoryColor: '#C9A84C',
    moduleId: 'llr', guideNumber: 3, totalGuides: 3,
    title: 'Exploiter les actions IA',
    subtitle: 'Utiliser les brouillons GBP, Google Ads et Landing Page générés pour agir vite sur un pic.',
    readTime: '7 min',
    prevArticle: 'llr-2',
    sections: [
      {
        title: 'Qu\'est-ce qu\'une action IA ?',
        content: 'Quand TremorApp détecte un pic d\'intensité, il génère automatiquement des contenus marketing prêts à l\'emploi adaptés au contexte. L\'objectif : réagir à une opportunité en quelques minutes plutôt qu\'en quelques heures.',
        table: {
          headers: ['Type d\'action', 'Ce qui est généré', 'Canal cible'],
          rows: [
            ['Post Google Business Profile', 'Post optimisé pour GBP avec texte et suggestion d\'image', 'Fiche Google My Business'],
            ['Annonce Google Ads', 'Titre + 2 descriptions + extensions', 'Campagne Google Search'],
            ['Landing Page', 'Structure complète d\'une page de conversion', 'Site web / CMS'],
          ],
        },
        tip: { type: 'warning', content: 'Les actions IA sont des points de départ, pas des contenus finaux. Ne publiez jamais un brouillon IA sans le relire et l\'adapter à votre voix de marque.' },
      },
      {
        title: 'Action 1 — Post Google Business Profile',
        content: 'Le brouillon GBP comprend : texte du post (150 à 300 caractères), appel à l\'action suggéré, suggestion de visuel, et hashtags/mots-clés locaux intégrés.',
        steps: [
          { number: 1, title: 'Relire', description: 'Lisez le texte généré et adaptez le ton à votre marque' },
          { number: 2, title: 'Personnaliser', description: 'Remplacez les [crochets] par vos informations réelles' },
          { number: 3, title: 'Visuel', description: 'Préparez une image correspondant à la suggestion visuelle' },
          { number: 4, title: 'Publier', description: 'Copiez → ouvrez business.google.com → nouveau Post → collez → publiez' },
        ],
        tip: { type: 'tip', content: 'Un post GBP publié dans les 2h suivant un pic capte l\'audience au moment où elle est la plus réceptive. Passé 6h, l\'efficacité diminue significativement.' },
        screenshotHint: '📸 Capture : section "Post GBP généré" d\'une alerte avec le contenu et le bouton "Utiliser"',
      },
      {
        title: 'Action 2 — Annonce Google Ads',
        content: 'Le brouillon Ads comprend : 3 titres (30 car. max), 2 descriptions (90 car. max), URL de destination suggérée, et extensions recommandées.',
        steps: [
          { number: 1, title: 'Vérifier', description: 'Le mot-clé cible dans les titres correspond au pic détecté' },
          { number: 2, title: 'Adapter', description: 'Adaptez la description à votre offre réelle et vos tarifs' },
          { number: 3, title: 'URL', description: 'Vérifiez que l\'URL de destination existe et est optimisée' },
          { number: 4, title: 'Créer', description: 'Ouvrez Google Ads → nouvelle annonce responsive → collez les titres et descriptions' },
          { number: 5, title: 'Budget', description: 'Définissez un budget quotidien supplémentaire pour les 48-72h du pic' },
        ],
        tip: { type: 'info', content: 'Si vous n\'avez pas de campagne Google Ads active, l\'action IA génère le contenu mais la publication nécessite un accès à votre compte Ads.' },
      },
      {
        title: 'Action 3 — Landing Page',
        content: 'TremorApp génère une structure complète de page de conversion : titre H1, sous-titre de réassurance, section bénéfices (3-5 arguments), section social proof, formulaire de contact, boutons CTA, et méta-title/description SEO.',
        steps: [
          { number: 1, title: 'Copier', description: 'Copiez la structure générée' },
          { number: 2, title: 'CMS', description: 'Créez une nouvelle page dans votre CMS (WordPress, Webflow, Wix...)' },
          { number: 3, title: 'Intégrer', description: 'Intégrez le contenu en remplaçant les [variables] par vos informations' },
          { number: 4, title: 'Branding', description: 'Ajoutez vos visuels, votre logo et vos couleurs de marque' },
          { number: 5, title: 'Publier', description: 'Publiez la page et mettez-la en avant dans votre annonce Google Ads' },
        ],
        tip: { type: 'tip', content: 'Les landing pages créées pendant un pic deviennent souvent vos meilleures pages SEO à long terme. Une page publiée en urgence pour "plombier urgence Paris 15" peut se positionner durablement.' },
        screenshotHint: '📸 Capture : section "Brief Landing Page" avec la structure générée',
      },
      {
        title: 'Personnaliser les actions IA',
        content: 'Configurez le profil IA de votre Radar pour des générations plus pertinentes :\n\n• Renseignez votre secteur d\'activité précis\n• Indiquez vos arguments de vente uniques\n• Définissez le ton : professionnel, proche, expert, urgence\n• Ajoutez vos informations de contact permanentes\n\nSi le contenu ne convient pas, cliquez "Régénérer" (jusqu\'à 5 fois par alerte). Chaque version est conservée.',
      },
    ],
  },
]
